import React, { useEffect, useState, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import {
    BarChart3,
    Users,
    Clock,
    Target,
    TrendingUp,
    Download,
    Search,
    Filter,
    Calendar,
    Building2,
    ChevronDown,
    ChevronRight,
    Euro,
    Info,
} from "lucide-react";
import * as XLSX from "xlsx";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";
import DataSourcePanel from "./DataSourcePanel";
import {
    Department,
    TaskEntry,
    EnhancedTimekeepingUser,
    DepartmentSummary,
} from "../types";

// ── Helpers ──────────────────────────────────────────────────

/** Duration in seconds → hours rounded to 2dp */
const secToHours = (sec: number) => Math.round((sec / 3600) * 100) / 100;

/** Get Monday of the current week */
const getStartOfWeek = (d: Date): Date => {
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.getFullYear(), d.getMonth(), diff);
};

/** Format date as YYYY-MM-DD for input fields */
const formatDateInput = (d: Date): string => d.toISOString().split("T")[0];

type DatePreset = "week" | "30days" | "ytd" | "custom";

/** Get dates for a given preset */
const getDatesForPreset = (preset: DatePreset): { start: string; end: string } => {
    const now = new Date();
    const end = formatDateInput(now);
    switch (preset) {
        case "week": {
            const monday = getStartOfWeek(now);
            monday.setDate(monday.getDate() - 7);
            const sunday = new Date(monday);
            sunday.setDate(sunday.getDate() + 6);
            return { start: formatDateInput(monday), end: formatDateInput(sunday) };
        }
        case "30days": {
            const d = new Date(now);
            d.setDate(d.getDate() - 30);
            return { start: formatDateInput(d), end };
        }
        case "ytd":
            return { start: formatDateInput(new Date(now.getFullYear(), 0, 1)), end };
        default:
            return { start: formatDateInput(new Date(now.getFullYear(), now.getMonth(), 1)), end };
    }
};

/**
 * MSL SKUs whose logged time is excused from utilisation: it represents time
 * the employee was unavailable to bill (leave/absence), so these hours are
 * subtracted from the target rather than dragging utilisation down. All other
 * non-billable SKUs (e.g. admin) stay in the target and lower utilisation.
 */
const UTILISATION_EXCUSED_SKUS = new Set<string>([
    "KIN-ABSENCE",
    "KIN-AL",
    "KIN-INLIEU",
    "KIN-INTERNALOPS",
    "KIN-PUBLICHOLIDAY",
    "KIN-STUDY",
    "KIN-TRAINING",
]);

/** Count weekdays between two dates (inclusive) */
const countWeekdays = (start: string, end: string): number => {
    const s = new Date(start);
    const e = new Date(end);
    let count = 0;
    const cur = new Date(s);
    while (cur <= e) {
        const day = cur.getDay();
        if (day !== 0 && day !== 6) count++;
        cur.setDate(cur.getDate() + 1);
    }
    return count;
};

// ── Custom table styles (matches Master Services List) ──────

const customStyles = {
    table: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            borderRadius: "8px",
        },
    },
    headRow: {
        style: {
            backgroundColor: "var(--bg-tertiary)",
            borderBottomColor: "var(--border-color)",
            color: "var(--text-primary)",
            fontWeight: 600,
            fontSize: "0.85rem",
        },
    },
    headCells: {
        style: {
            color: "var(--text-secondary)",
            paddingLeft: "16px",
            paddingRight: "16px",
        },
    },
    rows: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
        },
        highlightOnHoverStyle: {
            backgroundColor: "var(--bg-hover)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
        },
    },
    cells: {
        style: {
            color: "var(--text-primary)",
            paddingLeft: "16px",
            paddingRight: "16px",
        },
    },
    pagination: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-secondary)",
            borderTopColor: "var(--border-color)",
        },
        pageButtonsStyle: {
            color: "var(--text-secondary)",
            fill: "var(--text-secondary)",
            "&:hover:not(:disabled)": {
                backgroundColor: "var(--bg-hover)",
            },
            "&:disabled": {
                opacity: 0.4,
            },
        },
    },
    expanderRow: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-primary)",
        },
    },
    noData: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-secondary)",
        },
    },
};

// ── Expandable Row Sub-Component ────────────────────────────

interface ExpandProps {
    data: EnhancedTimekeepingUser;
}

const ExpandedEntries: React.FC<ExpandProps> = ({ data }) => (
    <div style={{ padding: "12px 24px" }}>
        <table className="data-table" style={{ width: "100%" }}>
            <thead>
                <tr>
                    <th>Date</th>
                    <th>Task</th>
                    <th>Client</th>
                    <th>Hours</th>
                    <th>Billable</th>
                    <th>Comment</th>
                </tr>
            </thead>
            <tbody>
                {data.entries.map((entry) => (
                    <tr key={entry.id}>
                        <td className="cell-secondary">
                            {new Date(entry.start_time).toLocaleDateString("en-IE")}
                        </td>
                        <td className="cell-primary">{entry.task_title || "—"}</td>
                        <td className="cell-secondary">{entry.business_name || "—"}</td>
                        <td className="cell-value">{secToHours(entry.duration).toFixed(2)}</td>
                        <td>
                            <span
                                className={`status-badge ${entry.task_billable ? "active" : "inactive"}`}
                            >
                                {entry.task_billable ? "Billable" : "Non-Billable"}
                            </span>
                        </td>
                        <td className="cell-secondary" style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {entry.comment || "—"}
                        </td>
                    </tr>
                ))}
                {data.entries.length === 0 && (
                    <tr>
                        <td colSpan={6} style={{ textAlign: "center", padding: 16, color: "var(--text-muted)" }}>
                            No entries in this period
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    </div>
);

// ── Main Component ──────────────────────────────────────────

const EnhancedTimekeeping: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();

    // Data state
    const [departments, setDepartments] = useState<Department[]>([]);
    const [rawEntries, setRawEntries] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);

    // Filter state
    const [dateRange, setDateRange] = useState<DatePreset>("week");
    const [startDate, setStartDate] = useState(() => getDatesForPreset("week").start);
    const [endDate, setEndDate] = useState(() => getDatesForPreset("week").end);
    const [selectedDepartment, setSelectedDepartment] = useState<string>("all");
    const [search, setSearch] = useState("");
    const [showHelp, setShowHelp] = useState(false);

    const handlePresetChange = (preset: DatePreset) => {
        setDateRange(preset);
        if (preset !== "custom") {
            const { start, end } = getDatesForPreset(preset);
            setStartDate(start);
            setEndDate(end);
        }
    };

    // ── Token helper ─────────────────────────────────────────
    const getToken = async () => {
        return acquireTokenSafe(instance, accounts);
    };

    // ── Fetch departments on mount ───────────────────────────
    useEffect(() => {
        const fetchDepartments = async () => {
            const token = await getToken();
            if (!token) return;
            try {
                const res = await fetch(`${getApiBaseUrl()}/api/enhanced-timekeeping/departments`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error("Failed to fetch departments");
                const data: Department[] = await res.json();
                setDepartments(data);
            } catch (err: any) {
                console.error("Dept fetch error:", err);
            }
        };
        fetchDepartments();
    }, [accounts]); // eslint-disable-line

    // ── Fetch entries when dates change ──────────────────────
    useEffect(() => {
        const fetchEntries = async () => {
            const token = await getToken();
            if (!token) return;
            setLoading(true);
            setError(null);
            try {
                const url = `${getApiBaseUrl()}/api/enhanced-timekeeping/data?startDate=${startDate}&endDate=${endDate}`;
                const res = await fetch(url, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error("Failed to fetch timekeeping data");
                const data = await res.json();
                setRawEntries(data);
            } catch (err: any) {
                console.error("Entries fetch error:", err);
                setError(err.message || "Failed to load data");
            } finally {
                setLoading(false);
            }
        };
        if (startDate && endDate) fetchEntries();
    }, [startDate, endDate, accounts]); // eslint-disable-line

    // ── Aggregate raw entries → per-user rows ────────────────
    const userRows: EnhancedTimekeepingUser[] = useMemo(() => {
        const map = new Map<number, EnhancedTimekeepingUser>();
        const weekdays = countWeekdays(startDate, endDate);
        const weeks = weekdays / 5;

        for (const row of rawEntries) {
            const uid: number = Number(row.admin_user_id);
            if (!map.has(uid)) {
                const userTargetPerWeek = row.user_target_hours
                    ? Number(row.user_target_hours)
                    : row.dept_target_hours
                    ? Number(row.dept_target_hours)
                    : 35;
                map.set(uid, {
                    userId: uid,
                    fullName: `${row.first_name || ""} ${row.last_name || ""}`.trim(),
                    email: row.email || "",
                    department: row.department_title || "Unassigned",
                    departmentId: row.department_id ? Number(row.department_id) : null,
                    totalHours: 0,
                    billableHours: 0,
                    nonBillableHours: 0,
                    excusedHours: 0,
                    utilisationRate: 0,
                    targetHours: Math.round(userTargetPerWeek * weeks * 100) / 100,
                    weeklyTargetHours: Math.round(userTargetPerWeek * 100) / 100,
                    entryCount: 0,
                    billableRate: row.billable_rate ? Number(row.billable_rate) : null,
                    revenue: 0,
                    entries: [],
                });
            }
            const user = map.get(uid)!;
            const hours = secToHours(row.duration || 0);
            user.totalHours += hours;
            if (row.task_billable) {
                user.billableHours += hours;
            } else {
                user.nonBillableHours += hours;
            }
            if (row.core_service_sku && UTILISATION_EXCUSED_SKUS.has(row.core_service_sku)) {
                user.excusedHours += hours;
            }
            user.entryCount += 1;
            user.entries.push({
                id: Number(row.id),
                start_time: row.start_time,
                end_time: row.end_time,
                duration: row.duration || 0,
                comment: row.comment,
                business_id: row.business_id,
                task_type_id: row.task_type_id,
                admin_user_id: uid,
                status: row.entry_status,
                rate: row.rate ? Number(row.rate) : null,
                core_service_id: row.core_service_id ? Number(row.core_service_id) : null,
                task_title: row.task_title,
                task_billable: row.task_billable,
                business_name: row.business_name,
                core_service_title: row.core_service_title,
                core_service_sku: row.core_service_sku,
            });
        }

        // Compute utilisation
        for (const user of map.values()) {
            user.totalHours = Math.round(user.totalHours * 100) / 100;
            user.billableHours = Math.round(user.billableHours * 100) / 100;
            user.nonBillableHours = Math.round(user.nonBillableHours * 100) / 100;
            user.excusedHours = Math.round(user.excusedHours * 100) / 100;
            // Strip excused (leave/absence) time from the target so it no longer
            // drags utilisation; floored at 0 for fully-excused periods.
            user.targetHours = Math.max(
                Math.round((user.targetHours - user.excusedHours) * 100) / 100,
                0
            );
            user.utilisationRate =
                user.targetHours > 0
                    ? Math.round((user.billableHours / user.targetHours) * 10000) / 100
                    : 0;
            user.revenue = user.billableRate ? Math.round(user.billableHours * user.billableRate * 100) / 100 : 0;
        }

        return Array.from(map.values()).sort((a, b) =>
            a.fullName.localeCompare(b.fullName)
        );
    }, [rawEntries, startDate, endDate]);

    // ── Apply department + search filters ────────────────────
    const filteredData = useMemo(() => {
        let data = userRows;
        if (selectedDepartment !== "all") {
            data = data.filter(
                (u) => String(u.departmentId) === selectedDepartment
            );
        }
        if (search) {
            const s = search.toLowerCase();
            data = data.filter(
                (u) =>
                    u.fullName.toLowerCase().includes(s) ||
                    u.email.toLowerCase().includes(s) ||
                    u.department.toLowerCase().includes(s)
            );
        }
        return data;
    }, [userRows, selectedDepartment, search]);

    // ── Department summaries ─────────────────────────────────
    const departmentSummaries: DepartmentSummary[] = useMemo(() => {
        const map = new Map<string, DepartmentSummary>();

        // Authoritative headcount from admin_department_roles (returned by
        // /api/enhanced-timekeeping/departments). This avoids the prior bug
        // where headcount counted only users who had logged time in the
        // selected window — so departments where nobody recorded hours
        // (or where staff are on leave / new starters) showed 0.
        const headcountByDeptId = new Map<number, number>();
        const headcountByTitle = new Map<string, number>();
        for (const d of departments) {
            const c = Number(d.member_count) || 0;
            headcountByDeptId.set(d.id, c);
            headcountByTitle.set(d.title, c);
        }

        // Seed with all active departments so depts with no entries
        // still appear (with 0 hours / 0% utilisation) — avoids
        // silently dropping departments where nobody recorded time.
        // Only seed when not filtering to a specific department.
        if (selectedDepartment === "all" && !search) {
            for (const d of departments) {
                map.set(d.title, {
                    department: d.title,
                    departmentId: d.id,
                    headcount: Number(d.member_count) || 0,
                    totalHours: 0,
                    billableHours: 0,
                    nonBillableHours: 0,
                    avgUtilisation: 0,
                    targetHours: 0,
                    revenue: 0,
                });
            }
        }

        for (const u of filteredData) {
            const key = u.department;
            if (!map.has(key)) {
                const seedHeadcount =
                    (u.departmentId != null && headcountByDeptId.get(u.departmentId)) ||
                    headcountByTitle.get(key) ||
                    0;
                map.set(key, {
                    department: key,
                    departmentId: u.departmentId,
                    headcount: seedHeadcount,
                    totalHours: 0,
                    billableHours: 0,
                    nonBillableHours: 0,
                    avgUtilisation: 0,
                    targetHours: 0,
                    revenue: 0,
                });
            }
            const s = map.get(key)!;
            // Headcount comes from admin_department_roles (already seeded
            // above); do NOT increment per-user-with-time-entries here.
            s.totalHours += u.totalHours;
            s.billableHours += u.billableHours;
            s.nonBillableHours += u.nonBillableHours;
            s.targetHours += u.targetHours;
            s.revenue += u.revenue;
        }
        for (const s of map.values()) {
            s.totalHours = Math.round(s.totalHours * 100) / 100;
            s.billableHours = Math.round(s.billableHours * 100) / 100;
            s.nonBillableHours = Math.round(s.nonBillableHours * 100) / 100;
            s.revenue = Math.round(s.revenue * 100) / 100;
            s.avgUtilisation =
                s.targetHours > 0
                    ? Math.round((s.billableHours / s.targetHours) * 10000) / 100
                    : 0;
        }
        return Array.from(map.values()).sort((a, b) =>
            a.department.localeCompare(b.department)
        );
    }, [filteredData, departments, selectedDepartment, search]);

    // ── Summary totals ───────────────────────────────────────
    const totals = useMemo(() => {
        const t = filteredData.reduce(
            (acc, u) => ({
                headcount: acc.headcount + 1,
                totalHours: acc.totalHours + u.totalHours,
                billableHours: acc.billableHours + u.billableHours,
                nonBillableHours: acc.nonBillableHours + u.nonBillableHours,
                targetHours: acc.targetHours + u.targetHours,
                revenue: acc.revenue + u.revenue,
            }),
            { headcount: 0, totalHours: 0, billableHours: 0, nonBillableHours: 0, targetHours: 0, revenue: 0 }
        );
        return {
            ...t,
            totalHours: Math.round(t.totalHours * 100) / 100,
            billableHours: Math.round(t.billableHours * 100) / 100,
            nonBillableHours: Math.round(t.nonBillableHours * 100) / 100,
            revenue: Math.round(t.revenue * 100) / 100,
            avgUtilisation:
                t.targetHours > 0
                    ? Math.round((t.billableHours / t.targetHours) * 10000) / 100
                    : 0,
        };
    }, [filteredData]);

    // ── Utilisation colour helper ────────────────────────────
    const utilisationColor = (rate: number): string => {
        if (rate >= 70) return "var(--status-success)";
        if (rate >= 50) return "var(--status-warning, #f0ad4e)";
        return "var(--status-error, #d9534f)";
    };

    // ── Table columns ────────────────────────────────────────
    const columns: TableColumn<EnhancedTimekeepingUser>[] = [
        {
            name: "Name",
            selector: (row) => row.fullName,
            sortable: true,
            grow: 2,
            cell: (row) => <span className="cell-primary">{row.fullName}</span>,
        },
        {
            name: "Department",
            selector: (row) => row.department,
            sortable: true,
            cell: (row) => <span className="cell-secondary">{row.department}</span>,
        },
        {
            name: "Total Hours",
            selector: (row) => row.totalHours,
            sortable: true,
            right: true,
            cell: (row) => <span className="cell-value">{row.totalHours.toFixed(2)}</span>,
        },
        {
            name: "Billable Hours",
            selector: (row) => row.billableHours,
            sortable: true,
            right: true,
            cell: (row) => (
                <span className="cell-value accent">{row.billableHours.toFixed(2)}</span>
            ),
        },
        {
            name: "Non-Billable",
            selector: (row) => row.nonBillableHours,
            sortable: true,
            right: true,
            cell: (row) => (
                <span className="cell-secondary">{row.nonBillableHours.toFixed(2)}</span>
            ),
        },
        {
            name: "Available Hours",
            selector: (row) => row.targetHours,
            sortable: true,
            right: true,
            cell: (row) => <span className="cell-secondary">{row.targetHours.toFixed(1)}</span>,
        },
        {
            name: "Target Hours",
            selector: (row) => row.weeklyTargetHours,
            sortable: true,
            right: true,
            cell: (row) => (
                <span className="cell-secondary" title="Weekly contractual target (admin_users.target_hours, with department/35h fallback)">
                    {row.weeklyTargetHours.toFixed(1)}
                </span>
            ),
        },
        {
            name: "Utilisation",
            selector: (row) => row.utilisationRate,
            sortable: true,
            right: true,
            cell: (row) =>
                row.targetHours > 0 ? (
                    <span
                        style={{
                            fontWeight: 600,
                            color: utilisationColor(row.utilisationRate),
                        }}
                    >
                        {row.utilisationRate.toFixed(1)}%
                    </span>
                ) : (
                    <span className="cell-secondary" title="No available hours after excused leave/absence">
                        N/A
                    </span>
                ),
        },
        {
            name: "Revenue",
            selector: (row) => row.revenue,
            sortable: true,
            right: true,
            cell: (row) => (
                <span className="cell-value accent">
                    {row.revenue > 0 ? `€${row.revenue.toLocaleString("en-IE", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : "—"}
                </span>
            ),
        },
        {
            name: "Entries",
            selector: (row) => row.entryCount,
            sortable: true,
            right: true,
            minWidth: "90px",
            cell: (row) => <span className="cell-secondary">{row.entryCount}</span>,
        },
    ];

    // ── Export: CSV ───────────────────────────────────────────
    const downloadCSV = () => {
        const headers = [
            "Full Name",
            "Department",
            "Total Hours",
            "Billable Hours",
            "Non-Billable Hours",
            "Available Hours",
            "Target Hours (weekly)",
            "Utilisation Rate (%)",
            "Entry Count",
        ];
        const rows = filteredData
            .map((r) =>
                [
                    `"${r.fullName.replace(/"/g, '""')}"`,
                    `"${r.department.replace(/"/g, '""')}"`,
                    r.totalHours.toFixed(2),
                    r.billableHours.toFixed(2),
                    r.nonBillableHours.toFixed(2),
                    r.targetHours.toFixed(1),
                    r.weeklyTargetHours.toFixed(1),
                    r.utilisationRate.toFixed(1),
                    r.entryCount,
                ].join(",")
            )
            .join("\n");
        const csvContent = `${headers.join(",")}\n${rows}`;
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `utilisation_report_${startDate}_${endDate}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // ── Export: XLSX ─────────────────────────────────────────
    const downloadXLSX = () => {
        const wb = XLSX.utils.book_new();

        // Sheet 1: Department Summary
        const deptData = departmentSummaries.map((d) => ({
            Department: d.department,
            Headcount: d.headcount,
            "Total Hours": d.totalHours,
            "Billable Hours": d.billableHours,
            "Non-Billable Hours": d.nonBillableHours,
            "Available Hours": Math.round(d.targetHours * 10) / 10,
            "Avg Utilisation (%)": d.avgUtilisation,
            "Revenue (€)": d.revenue,
        }));
        const ws1 = XLSX.utils.json_to_sheet(deptData);
        XLSX.utils.book_append_sheet(wb, ws1, "Department Summary");

        // Sheet 2: User Detail
        const userData = filteredData.map((u) => ({
            "Full Name": u.fullName,
            Department: u.department,
            "Total Hours": u.totalHours,
            "Billable Hours": u.billableHours,
            "Non-Billable Hours": u.nonBillableHours,
            "Available Hours": u.targetHours,
            "Target Hours (weekly)": u.weeklyTargetHours,
            "Utilisation Rate (%)": u.utilisationRate,
            "Revenue (€)": u.revenue,
            "Entry Count": u.entryCount,
        }));
        const ws2 = XLSX.utils.json_to_sheet(userData);
        XLSX.utils.book_append_sheet(wb, ws2, "User Detail");

        XLSX.writeFile(wb, `utilisation_report_${startDate}_${endDate}.xlsx`);
    };

    // ── Render ───────────────────────────────────────────────
    return (
        <div className="page-container">
            <div className="page-header compact">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div className="page-title">
                        <BarChart3 size={20} />
                        <h1>Utilisation Report</h1>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                        <button
                            className="download-btn"
                            onClick={() => setShowHelp((v) => !v)}
                            title="Notes on Utilisation"
                        >
                            <Info size={16} /> {showHelp ? "Hide details" : "Notes on Utilisation"}
                        </button>
                        <button className="download-btn" onClick={downloadCSV} title="Export as CSV">
                            <Download size={16} /> CSV
                        </button>
                        <button className="download-btn" onClick={downloadXLSX} title="Export as XLSX">
                            <Download size={16} /> XLSX
                        </button>
                    </div>
                </div>
            </div>

            {showHelp && (
                <div
                    style={{
                        background: "var(--bg-secondary)",
                        border: "1px solid var(--border-color)",
                        borderLeft: "3px solid var(--accent-primary)",
                        borderRadius: 6,
                        padding: "14px 18px",
                        marginBottom: 20,
                        color: "var(--text-secondary)",
                        fontSize: 13,
                        lineHeight: 1.55,
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, color: "var(--text-primary)", fontWeight: 600, fontSize: 14 }}>
                        <Info size={16} /> How utilisation is calculated
                    </div>
                    <p style={{ margin: "0 0 10px" }}>
                        <strong style={{ color: "var(--text-primary)" }}>Formula:</strong>{" "}
                        <code style={{ background: "var(--bg-tertiary)", padding: "1px 6px", borderRadius: 4 }}>
                            utilisation % = (billable hours ÷ available hours) × 100
                        </code>
                    </p>
                    <p style={{ margin: "0 0 10px" }}>
                        Utilisation answers a single question:{" "}
                        <em>of the hours this person was realistically available to bill, what fraction did they
                        actually bill?</em> Two inputs drive it — the billable hours they logged (top of the
                        fraction) and the available hours they are measured against (bottom). The two columns in
                        the table reflect these directly: <strong>Available Hours</strong> is the denominator;{" "}
                        <strong>Target Hours</strong> is the per-employee weekly contract that available hours is
                        derived from. The order below is the order the calculation runs in.
                    </p>
                    <ol style={{ margin: "0 0 10px 20px", padding: 0 }}>
                        <li style={{ marginBottom: 8 }}>
                            <strong style={{ color: "var(--text-primary)" }}>Billable hours (the numerator).</strong>{" "}
                            The sum of duration on time entries whose task is billable (
                            <code>task_billable = true</code>). This is the <em>only</em> figure that can push
                            utilisation up. Non-billable time never adds to it — to raise the number, a person must
                            log more billable work.
                        </li>
                        <li style={{ marginBottom: 8 }}>
                            <strong style={{ color: "var(--text-primary)" }}>Weekly target hours (per-employee contract).</strong>{" "}
                            The hours per week the employee is contracted to work — shown in the{" "}
                            <strong>Target Hours</strong> column. Source priority:
                            <ol style={{ margin: "4px 0 4px 20px", padding: 0 }}>
                                <li>Per-user target (<code>admin_users.target_hours</code>) if set</li>
                                <li>Otherwise the department default (<code>departments.target_hours</code>)</li>
                                <li>Otherwise a fallback of 35 hours/week</li>
                            </ol>
                        </li>
                        <li style={{ marginBottom: 8 }}>
                            <strong style={{ color: "var(--text-primary)" }}>Available hours (the denominator).</strong>{" "}
                            Take the weekly target × the number of weeks in the selected range, then subtract any
                            time logged against an excused SKU. The result — shown in the{" "}
                            <strong>Available Hours</strong> column — is the time the person was realistically
                            available to bill during the period. Weeks in range = (weekdays between start and end)
                            ÷ 5; weekends never count.
                            <div style={{ margin: "8px 0", padding: "8px 12px", background: "var(--bg-tertiary)", borderRadius: 4, fontFamily: "monospace", fontSize: 12 }}>
                                available hours = (weekly target × weeks) − hours on excused SKUs &nbsp;(floored at 0)
                            </div>
                        </li>
                        <li style={{ marginBottom: 8 }}>
                            <strong style={{ color: "var(--text-primary)" }}>Excused time is invisible to the ratio (this is the key step).</strong>{" "}
                            Some time is logged against activities where the person was genuinely{" "}
                            <em>not available</em> to do billable work — leave, public holidays, approved
                            non-billable internal time, and so on. Rather than leave that time sitting in the
                            denominator dragging the percentage down, we subtract those hours from available hours
                            entirely, so they neither help nor harm utilisation. The SKUs that are excused — i.e.
                            that do <strong style={{ color: "var(--text-primary)" }}>NOT</strong> harm utilisation
                            — are:
                            <div style={{ margin: "6px 0 0" }}>
                                {Array.from(UTILISATION_EXCUSED_SKUS)
                                    .sort()
                                    .map((sku) => (
                                        <code
                                            key={sku}
                                            style={{
                                                background: "var(--bg-tertiary)",
                                                padding: "1px 6px",
                                                borderRadius: 4,
                                                marginRight: 6,
                                                marginBottom: 4,
                                                display: "inline-block",
                                            }}
                                        >
                                            {sku}
                                        </code>
                                    ))}
                            </div>
                        </li>
                        <li style={{ marginBottom: 8 }}>
                            <strong style={{ color: "var(--text-primary)" }}>Every other non-billable SKU still lowers utilisation.</strong>{" "}
                            Any non-billable time <em>not</em> in the excused list above stays inside available
                            hours. The person was on the clock but not billing, so that time legitimately pulls the
                            percentage down. This deliberately includes categories such as{" "}
                            <code>KIN-SICK</code>, <code>KIN-MATERNITYPATERNITY</code>,{" "}
                            <code>KIN-COMPASSIONATE</code>, <code>KIN-JURYSERVICE</code>,{" "}
                            <code>KIN-MEDICAL</code> and <code>KIN-UNPAID</code> — these are <em>not</em> excused
                            and a week dominated by them will read low.
                        </li>
                        <li style={{ marginBottom: 8 }}>
                            <strong style={{ color: "var(--text-primary)" }}>Fully-excused periods show “N/A”.</strong>{" "}
                            If excused time cancels out the whole window (e.g. someone on leave for the entire
                            range), there are no available hours left to measure against. Utilisation then shows{" "}
                            <strong style={{ color: "var(--text-primary)" }}>N/A</strong> rather than a misleading
                            0%, and that person contributes nothing to the department and overall averages.
                        </li>
                        <li style={{ marginBottom: 8 }}>
                            <strong style={{ color: "var(--text-primary)" }}>Department &amp; overall averages are hours-weighted.</strong>{" "}
                            The average in the KPI strip and the department breakdown is{" "}
                            <code>Σ billable hours ÷ Σ available hours</code> across the group — <em>not</em>{" "}
                            the simple mean of each individual's percentage. People with larger available windows
                            therefore move the average more.
                        </li>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>Revenue</strong> = billable hours ×
                            the user's <code>billable_rate</code> from{" "}
                            <a href="/employee-rates" style={{ color: "var(--accent-primary)" }}>Employee Rates</a>.
                            Users with no rate set contribute 0 to revenue. Revenue is independent of the
                            available-hours adjustments above.
                        </li>
                    </ol>
                    <div style={{ margin: "10px 0", padding: "10px 14px", background: "var(--bg-tertiary)", borderRadius: 6, fontSize: 12 }}>
                        <strong style={{ color: "var(--text-primary)" }}>Worked example.</strong> A person on a
                        35h weekly target (Target Hours = 35) who bills 21h in a week:
                        <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
                            <li>
                                Takes 14h annual leave (<code>KIN-AL</code>, excused): available hours = 35 − 14 =
                                21h → utilisation = 21 ÷ 21 ={" "}
                                <strong style={{ color: "var(--text-primary)" }}>100%</strong>. The leave did{" "}
                                <strong>not</strong> penalise them.
                            </li>
                            <li>
                                Instead logs 14h sick (<code>KIN-SICK</code>, not excused): available hours stays
                                at 35h → utilisation = 21 ÷ 35 ={" "}
                                <strong style={{ color: "var(--text-primary)" }}>60%</strong>. The sick time{" "}
                                <strong>does</strong> lower utilisation.
                            </li>
                        </ul>
                    </div>
                    <p style={{ margin: "10px 0 0", color: "var(--text-muted)", fontSize: 12 }}>
                        Note: a healthy utilisation is typically 70–85%. Values above 100% mean the user logged
                        more billable hours than their available hours for the period.
                    </p>
                </div>
            )}

            {/* Date Presets + Filters */}
            <div className="table-controls" style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    {(["week", "30days", "ytd", "custom"] as DatePreset[]).map((preset) => (
                        <button
                            key={preset}
                            className={`filter-select${dateRange === preset ? " active" : ""}`}
                            style={{
                                cursor: "pointer",
                                padding: "6px 12px",
                                border: dateRange === preset ? "1px solid var(--accent-primary)" : "1px solid var(--border-color)",
                                borderRadius: 6,
                                background: dateRange === preset ? "var(--accent-primary)" : "var(--bg-secondary)",
                                color: dateRange === preset ? "#fff" : "var(--text-secondary)",
                                fontWeight: dateRange === preset ? 600 : 400,
                                fontSize: 13,
                            }}
                            onClick={() => handlePresetChange(preset)}
                        >
                            {preset === "week" ? "Past Week" : preset === "30days" ? "Last 30 Days" : preset === "ytd" ? "YTD" : "Custom"}
                        </button>
                    ))}
                </div>

                {dateRange === "custom" && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Calendar size={16} style={{ color: "var(--text-muted)" }} />
                        <input
                            type="date"
                            className="search-input"
                            style={{ minWidth: 150 }}
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                        <span style={{ color: "var(--text-muted)" }}>to</span>
                        <input
                            type="date"
                            className="search-input"
                            style={{ minWidth: 150 }}
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                )}

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Building2 size={16} style={{ color: "var(--text-muted)" }} />
                    <select
                        className="filter-select"
                        value={selectedDepartment}
                        onChange={(e) => setSelectedDepartment(e.target.value)}
                    >
                        <option value="all">All Departments</option>
                        {departments.map((d) => (
                            <option key={d.id} value={String(d.id)}>
                                {d.title}
                            </option>
                        ))}
                    </select>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Search size={16} style={{ color: "var(--text-muted)" }} />
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search by name, email, or department..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* Error */}
            {error && (
                <div style={{ padding: "12px 16px", marginBottom: 16, borderRadius: 8, background: "var(--status-error-bg, rgba(217,83,79,0.1))", color: "var(--status-error, #d9534f)", border: "1px solid var(--status-error, #d9534f)" }}>
                    {error}
                </div>
            )}

            {/* Summary Cards */}
            <div className="stats-grid" style={{ marginBottom: 24 }}>
                <div className="stat-card">
                    <div className="stat-icon blue">
                        <Users size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{totals.headcount}</div>
                        <div className="stat-label">Headcount</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon purple">
                        <Clock size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{totals.totalHours.toFixed(1)}</div>
                        <div className="stat-label">Total Hours</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon green">
                        <TrendingUp size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{totals.billableHours.toFixed(1)}</div>
                        <div className="stat-label">Billable Hours</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon orange">
                        <Clock size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{totals.nonBillableHours.toFixed(1)}</div>
                        <div className="stat-label">Non-Billable Hours</div>
                    </div>
                </div>
                <div className="stat-card highlight">
                    <div className="stat-icon yellow">
                        <Target size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{totals.avgUtilisation.toFixed(1)}%</div>
                        <div className="stat-label">Avg Utilisation</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon green">
                        <Euro size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">
                            €{totals.revenue.toLocaleString("en-IE", { maximumFractionDigits: 0 })}
                        </div>
                        <div className="stat-label">Revenue (billable)</div>
                    </div>
                </div>
            </div>

            {/* Department Summary Table (when viewing all departments) */}
            {selectedDepartment === "all" && departmentSummaries.length > 1 && (
                <div className="data-table-container" style={{ marginBottom: 24 }}>
                    <h3 style={{ margin: "0 0 12px", color: "var(--text-primary)", fontSize: 16, fontWeight: 600 }}>
                        <Building2 size={18} style={{ verticalAlign: "middle", marginRight: 8 }} />
                        Department Breakdown
                    </h3>
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th className="text-left">Department</th>
                                <th className="text-right">Headcount</th>
                                <th className="text-right">Total Hours</th>
                                <th className="text-right">Billable Hours</th>
                                <th className="text-right">Non-Billable</th>
                                <th className="text-right">Avg Utilisation</th>
                                <th className="text-right">Revenue</th>
                            </tr>
                        </thead>
                        <tbody>
                            {departmentSummaries.map((d) => (
                                <tr key={d.department}>
                                    <td className="cell-primary">{d.department}</td>
                                    <td className="text-right cell-secondary">{d.headcount}</td>
                                    <td className="text-right cell-value">{d.totalHours.toFixed(1)}</td>
                                    <td className="text-right cell-value accent">{d.billableHours.toFixed(1)}</td>
                                    <td className="text-right cell-secondary">{d.nonBillableHours.toFixed(1)}</td>
                                    <td className="text-right" style={{ fontWeight: 600, color: utilisationColor(d.avgUtilisation) }}>
                                        {d.avgUtilisation.toFixed(1)}%
                                    </td>
                                    <td className="text-right cell-value accent">
                                        {d.revenue > 0 ? `€${d.revenue.toLocaleString("en-IE", { maximumFractionDigits: 0 })}` : "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Main Data Table */}
            <div className="data-table-container">
                <DataTable
                    columns={columns}
                    data={filteredData}
                    customStyles={customStyles}
                    pagination
                    paginationPerPage={25}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    highlightOnHover
                    defaultSortFieldId={1}
                    expandableRows
                    expandableRowsComponent={ExpandedEntries}
                    noDataComponent={
                        <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
                            {rawEntries.length === 0
                                ? "No time entries found for the selected date range."
                                : "No results match the current filters."}
                        </div>
                    }
                />
            </div>

            <DataSourcePanel
                summary={
                    <>
                        Live mode aggregates raw entries directly from the MKP{" "}
                        <code>task_entries</code> table for the selected date range.
                    </>
                }
                sections={[
                    {
                        title: "APIs called",
                        items: [
                            {
                                name: "GET /api/enhanced-timekeeping/data",
                                description: (
                                    <>
                                        Joins <code>task_entries</code> &times;{" "}
                                        <code>admin_users</code> &times;{" "}
                                        <code>admin_department_roles</code> &times;{" "}
                                        <code>departments</code> &times; <code>task_types</code>{" "}
                                        &times; <code>businesses</code> &times;{" "}
                                        <code>core_services</code>. Filters to{" "}
                                        <code>status IN ('submitted','approved')</code>. Each user
                                        is mapped to a single primary department via a LATERAL
                                        pick that prefers head &gt; manager &gt; lead &gt; member.
                                    </>
                                ),
                            },
                            {
                                name: "GET /api/enhanced-timekeeping/departments",
                                description: (
                                    <>
                                        Returns active departments (used for the dropdown filter
                                        and to seed empty rows in the Department Breakdown).
                                    </>
                                ),
                            },
                        ],
                    },
                    {
                        title: "Tables read (MKP Postgres)",
                        items: [
                            {
                                name: "task_entries",
                                description:
                                    "Raw time entries (start/end, duration, billability, task type, status).",
                            },
                            {
                                name: "admin_users",
                                description:
                                    "Employee identity and target hours per week.",
                            },
                            {
                                name: "admin_department_roles",
                                description:
                                    "Many-to-many mapping of employees to departments with role_type.",
                            },
                            {
                                name: "departments",
                                description:
                                    "Department titles, default target_hours, status flag.",
                            },
                        ],
                    },
                    {
                        title: "Derivation",
                        items: [
                            {
                                name: "Target hours (weekly)",
                                description: (
                                    <>
                                        Per-user contract from{" "}
                                        <code>admin_users.target_hours</code> if set; otherwise the
                                        department default (<code>departments.target_hours</code>);
                                        otherwise a 35h/week fallback. Shown as-is in the Target Hours
                                        column.
                                    </>
                                ),
                            },
                            {
                                name: "Available hours",
                                description: (
                                    <>
                                        Weekly target × weekdays-in-range / 5, then subtract hours
                                        logged against excused SKUs (<code>KIN-AL</code>,{" "}
                                        <code>KIN-ABSENCE</code>, <code>KIN-INLIEU</code>,{" "}
                                        <code>KIN-INTERNALOPS</code>, <code>KIN-PUBLICHOLIDAY</code>,{" "}
                                        <code>KIN-STUDY</code>, <code>KIN-TRAINING</code>) so excused
                                        time does not drag utilisation. Other non-billable time (e.g.{" "}
                                        <code>KIN-ADMIN</code>, <code>KIN-SICK</code>,{" "}
                                        <code>KIN-MATERNITYPATERNITY</code>) stays inside available
                                        hours and lowers utilisation.
                                    </>
                                ),
                            },
                            {
                                name: "Utilisation",
                                description:
                                    "billable_hours / available_hours, expressed as %. Shows N/A when the period is fully excused.",
                            },
                            {
                                name: "Revenue",
                                description: (
                                    <>
                                        billable_hours &times;{" "}
                                        <code>admin_users.billable_rate</code>.
                                    </>
                                ),
                            },
                        ],
                    },
                ]}
            />
        </div>
    );
};

export default EnhancedTimekeeping;
